import { useState } from "react";
import { Empty, List, App } from "antd";
import { FileTextOutlined, FilePdfOutlined } from "@ant-design/icons";
import type { HistoryFnaVO } from "@ifa/utils";
import { downloadOrderFormPdf } from "@ifa/utils";
import CustomButton from "../../../../../../components/CustomButton";
import styles from "./FnaHistoryPlaceholder.module.css";
import pdfIocn from "../../../../../../assets/img/pdf.png";

type Props = {
  historyFnaList?: HistoryFnaVO[];
};

export function FnaHistoryPlaceholder({ historyFnaList }: Props) {
  const { message } = App.useApp();
  const [previewing, setPreviewing] = useState<Record<number, boolean>>({});
  const hasData = historyFnaList && historyFnaList.length > 0;

  const handlePreview = async (item: HistoryFnaVO, index: number) => {
    if (!item.orderId) {
      message.warning("無法獲取訂單資料");
      return;
    }

    if (!item.formType) {
      message.warning("無法獲取表單類型");
      return;
    }

    try {
      setPreviewing((prev) => ({ ...prev, [index]: true }));

      // 调用 downloadOrderFormPdf 接口获取文件
      const result = await downloadOrderFormPdf({
        formType: item.formType,
        orderId: item.orderId,
      });

      // 创建 Blob URL 用于预览
      const blobUrl = URL.createObjectURL(result.blob);

      // 在新窗口中打开 PDF 预览
      const previewWindow = window.open(blobUrl, "_blank");

      if (!previewWindow) {
        message.warning("請允許彈出窗口以預覽檔案");
        URL.revokeObjectURL(blobUrl);
        return;
      }

      // 当窗口关闭时释放 URL
      previewWindow.addEventListener("beforeunload", () => {
        URL.revokeObjectURL(blobUrl);
      });

      message.success("正在打開預覽窗口");
    } catch (error) {
      console.error("Failed to preview FNA file:", error);
      message.error("檔案預覽失敗，請重試");
    } finally {
      setPreviewing((prev) => ({ ...prev, [index]: false }));
    }
  };

  return (
    <>
      {hasData ? (
        <List
          grid={{ gutter: 16, column: 2 }}
          dataSource={historyFnaList}
          renderItem={(item, index) => (
            <List.Item>
              <div className={styles.fnaCard}>
                {/* 卡片标题：订单号 */}
                <div className={styles.cardTitle}>
                  <div className={styles.titleIcon}>
                    <FileTextOutlined />
                  </div>
                  <span className={styles.titleText}>訂單號：{item.orderNo || "-"}</span>
                </div>

                {/* 卡片内容 */}
                <div className={styles.cardContent}>
                  <div className={styles.infoRow}>
                    <span className={styles.label}>保單號：</span>
                    <span className={styles.value}>{item.policyNo || "-"}</span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>投保FNA檔案：</span>
                    <span className={styles.fileValue}>
                      <img className={styles.pdfIcon} src={pdfIocn} alt="pdf" />
                      <span style={{ flex: 1, width: "0", wordWrap: "break-word" }}>{item.fileName || "-"}</span>
                    </span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>更新時間：</span>
                    <span className={styles.value}>{item.signTime || "-"}</span>
                  </div>
                </div>

                {/* 底部按钮 */}
                <CustomButton height="44px" className={styles.viewButton} onClick={() => handlePreview(item, index)} loading={previewing[index]} block={true} bgColor="#00C7B6">
                  查看FNA檔案
                </CustomButton>
              </div>
            </List.Item>
          )}
        />
      ) : (
        <div style={{ padding: "40px 0", textAlign: "center" }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暫無簽署記錄" />
        </div>
      )}
    </>
  );
}
